import { QueryParams } from 'common/urls/QueryParams';
import { useMemo } from 'react';
import { BrowseFilter } from 'src/gqlReactTypings.generated.d';
import { LandingMarketSegment } from 'src/shared/hooks/useMarketSegment';
import { match } from 'ts-pattern';
import { DietaryTags } from 'web-common/src/shared/Enums';
import {
  CHINESE_COMMUNITY_REVIEWS,
  CUSTOMER_VOICES_REVIEWS,
  DIETARY_REVIEWS,
  HALAL_COMMUNITY_REVIEWS,
  INDIAN_COMMUNITY_REVIEWS,
  MEAL_PLAN_CUSTOMER_VOICES_REVIEWS,
  YOUNG_FAMILY_REVIEWS,
} from './components/CustomerVoices/placeholderConsts';
import { PatternedStrip } from './components/shared/PatternedStrip';
import { TastesLikeHome } from './components/TastesLikeHome/TastesLikeHome';

export const useMarketSegmentContent = (marketSegment: LandingMarketSegment, isMealPlan: boolean) =>
  useMemo(() => {
    const exploreQueryParams = match({ marketSegment })
      .with({ marketSegment: 'indian-community' }, () => ({
        [QueryParams.CUISINE_FILTER]: 'INDIAN',
        [QueryParams.BROWSE_FILTER]: BrowseFilter.Cuisine,
      }))
      .with({ marketSegment: 'chinese-community' }, () => ({
        [QueryParams.CUISINE_FILTER]: 'CHINESE',
        [QueryParams.BROWSE_FILTER]: BrowseFilter.Cuisine,
      }))
      .with({ marketSegment: 'halal-community' }, () => ({
        [QueryParams.DIETARY_FILTER]: DietaryTags.HALAL,
        [QueryParams.BROWSE_FILTER]: BrowseFilter.Dietary,
      }))
      .otherwise(() => ({}));

    const customerVoices =
      isMealPlan && !marketSegment?.includes('meal-plan')
        ? MEAL_PLAN_CUSTOMER_VOICES_REVIEWS
        : match({ marketSegment })
            .with({ marketSegment: 'dietary-restrictions' }, () => DIETARY_REVIEWS)
            .with({ marketSegment: 'kid-friendly' }, () => YOUNG_FAMILY_REVIEWS)
            .with({ marketSegment: 'indian-community' }, () => INDIAN_COMMUNITY_REVIEWS)
            .with({ marketSegment: 'chinese-community' }, () => CHINESE_COMMUNITY_REVIEWS)
            .with({ marketSegment: 'halal-community' }, () => HALAL_COMMUNITY_REVIEWS)
            .otherwise(() => CUSTOMER_VOICES_REVIEWS);

    const patternedStrip = match({ marketSegment })
      .with(
        { marketSegment: 'indian-community' },
        { marketSegment: 'indian-meal-plan' },
        { marketSegment: 'yaf-dink' },
        () => (
          <PatternedStrip backgroundUrl='https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/indian-cuisine/pattern-strips/landing-page.svg' />
        )
      )
      .with({ marketSegment: 'chinese-community' }, () => (
        <PatternedStrip backgroundUrl='https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/chinese-cuisine/pattern-strips/landing-page.svg' />
      ))
      .with({ marketSegment: 'halal-community' }, () => (
        <PatternedStrip
          className='mb-10'
          backgroundUrl='https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/halal-dietary/pattern-strips/landing-page.svg'
        />
      ))
      .otherwise(() => <div className='h-10 md:h-16' />);

    const tastesLikeHome = match({ marketSegment })
      .with({ marketSegment: 'indian-meal-plan' }, () => <TastesLikeHome />)
      .with({ marketSegment: 'indian-community' }, () => <TastesLikeHome />)
      .with({ marketSegment: 'chinese-community' }, () => null) // NOTE: This is still in design
      .with({ marketSegment: 'halal-community' }, () => null)
      .otherwise(() => null);

    return {
      exploreQueryParams,
      customerVoices,
      patternedStrip,
      tastesLikeHome,
    };
  }, [marketSegment]);
